import { collection, doc, onSnapshot, orderBy, query, deleteDoc, getDoc, where, Timestamp, getDocs } from 'firebase/firestore'
import { useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { db } from '../../firebase'
import { localizeCategory } from '../../utils/categoryResolver'
import { 
  Box, 
  Button, 
  Stack, 
  Typography, 
  Pagination,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Snackbar,
  Alert,
  TextField,
  InputAdornment,
  Grid
} from '@mui/material'
import { 
  Delete as DeleteIcon,
  FilterList as FilterIcon,
  Search as SearchIcon,
  Visibility as VisibilityIcon,
  Description as DescriptionIcon
} from '@mui/icons-material'

interface OrderDoc {
  id: string
  productName?: string
  status?: string
  buyerId?: string
  createdAt?: any
  updatedAt?: any
  productCategory?: string
  productSpecs?: any
  location?: { lat?: number; lng?: number; latitude?: number; longitude?: number }
  rangeKm?: number
  notes?: string
  /** تفاصيل إضافية عن المنتج التي يدخلها المشتري */
  additionalProductDetails?: string
  expiresAt?: any
  productId?: string
}

interface BuyerInfo {
  name?: string
  phone?: string
}

interface SellerInfo {
  name?: string
  phone?: string
}

interface OffersCountInfo {
  count: number
  loading: boolean
}

interface OrderStatusInfo {
  buyerStatus: string | null
  sellerStatus: string | null
  loading: boolean
}

type OrderStatus = 'pending' | 'confirmed' | 'cancelled' | 'completed' | 'received' | 'expired' | 'all'

const statusMap: Record<string, { label: string; color: 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning' }> = {
  'pending': { label: 'الطلب جديد', color: 'warning' },
  'cancelled': { label: 'تم حذف الطلب', color: 'error' },
  'confirmed': { label: 'تم الدفع', color: 'info' },
  'completed': { label: 'تم الاستلام', color: 'success' },
  'received': { label: 'تم الاستلام', color: 'success' },
  'delivered': { label: 'تم التسليم', color: 'success' },
  'expired': { label: 'منتهي الصلاحية', color: 'default' }
}

export default function OrdersPage() {
  const uiLang: 'ar' | 'en' = (localStorage.getItem('i18nextLng') || '').startsWith('en') ? 'en' : 'ar'
  const [searchParams, setSearchParams] = useSearchParams()
  const navigate = useNavigate()
  const [rows, setRows] = useState<OrderDoc[]>([])
  const [buyerInfoMap, setBuyerInfoMap] = useState<Record<string, BuyerInfo>>({})
  const [sellerInfoMap, setSellerInfoMap] = useState<Record<string, SellerInfo>>({})
  const [offersCountMap, setOffersCountMap] = useState<Record<string, OffersCountInfo>>({})
  const [orderStatusMap, setOrderStatusMap] = useState<Record<string, OrderStatusInfo>>({})
  const [orderSellerMap, setOrderSellerMap] = useState<Record<string, string>>({}) // orderId -> sellerId
  const [page, setPage] = useState(1)
  const [pageSize] = useState(20)
  const [statusFilter, setStatusFilter] = useState<OrderStatus>('all')
  const [selectedOrders, setSelectedOrders] = useState<string[]>([])
  const [deleteDialog, setDeleteDialog] = useState<{ open: boolean; orderId?: string; multiple?: boolean }>({ open: false })
  const [orderDetailsDialog, setOrderDetailsDialog] = useState<{ open: boolean; orderId?: string }>({ open: false })
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' })
  const [searchQuery, setSearchQuery] = useState('')

  useEffect(() => {
    const q = query(collection(db, 'requests'), orderBy('createdAt', 'desc'))
    const unsub = onSnapshot(q, async (snapshot) => {
      const rawOrders: OrderDoc[] = snapshot.docs.map(d => ({
        id: d.id, 
        ...(d.data() as any)
      }))
      const orders: OrderDoc[] = await Promise.all(rawOrders.map(async (o) => {
        const localizedCategory = o.productCategory
          ? await localizeCategory(String(o.productCategory), uiLang)
          : o.productCategory
        return {
          ...o,
          productCategory: localizedCategory
        }
      }))
      setRows(orders)

      // جلب معلومات المشترين
      const buyerIds = [...new Set(orders.map(o => o.buyerId).filter(Boolean) as string[])]
      const buyerInfo: Record<string, BuyerInfo> = {}
      
      await Promise.all(
        buyerIds.map(async (buyerId) => {
          try {
            const buyerDoc = await getDoc(doc(db, 'users', buyerId))
            if (buyerDoc.exists()) {
              const data = buyerDoc.data()
              buyerInfo[buyerId] = {
                name: data.name || 'غير محدد',
                phone: data.phone || 'غير متوفر'
              }
            } else {
              buyerInfo[buyerId] = {
                name: 'غير محدد',
                phone: 'غير متوفر'
              }
            }
          } catch (error) {
            console.error(`خطأ في جلب بيانات المشتري ${buyerId}:`, error)
            buyerInfo[buyerId] = {
              name: 'غير محدد',
              phone: 'غير متوفر'
            }
          }
        })
      )
      
      setBuyerInfoMap(buyerInfo)

      // جلب معلومات التجار وربط الطلبات بالتجار
      const sellerInfo: Record<string, SellerInfo> = {}
      const orderSeller: Record<string, string> = {}
      await Promise.all(
        orders.map(async (order) => {
          try {
            // البحث عن العرض المقبول لهذا الطلب
            const offersQuery = query(
              collection(db, 'offers'),
              where('requestId', '==', order.id),
              where('status', '==', 'accepted')
            )
            const offersSnapshot = await getDocs(offersQuery)
            
            if (!offersSnapshot.empty) {
              const acceptedOffer = offersSnapshot.docs[0].data()
              const sellerId = acceptedOffer.sellerId
              
              if (sellerId) {
                // ربط الطلب بالتاجر
                orderSeller[order.id] = sellerId
                
                // جلب معلومات التاجر إذا لم يتم جلبها من قبل
                if (!sellerInfo[sellerId]) {
                  try {
                    const sellerDoc = await getDoc(doc(db, 'users', sellerId))
                    if (sellerDoc.exists()) {
                      const data = sellerDoc.data()
                      sellerInfo[sellerId] = {
                        name: data.name || 'غير محدد',
                        phone: data.phone || 'غير متوفر'
                      }
                    } else {
                      sellerInfo[sellerId] = {
                        name: 'غير محدد',
                        phone: 'غير متوفر'
                      }
                    }
                  } catch (error) {
                    console.error(`خطأ في جلب بيانات التاجر ${sellerId}:`, error)
                    sellerInfo[sellerId] = {
                      name: 'غير محدد',
                      phone: 'غير متوفر'
                    }
                  }
                }
              }
            }
          } catch (error) {
            console.error(`خطأ في جلب معلومات التاجر للطلب ${order.id}:`, error)
          }
        })
      )
      
      setSellerInfoMap(sellerInfo)
      setOrderSellerMap(orderSeller)

      // جلب عدد العروض المستلمة لكل طلب
      const offersCountInfo: Record<string, OffersCountInfo> = {}
      await Promise.all(
        orders.map(async (order) => {
          offersCountInfo[order.id] = { count: 0, loading: true }
          try {
            const offersQuery = query(
              collection(db, 'offers'),
              where('requestId', '==', order.id)
            )
            const offersSnapshot = await getDocs(offersQuery)
            offersCountInfo[order.id] = { count: offersSnapshot.size, loading: false }
          } catch (error) {
            console.error(`خطأ في جلب عدد العروض للطلب ${order.id}:`, error)
            offersCountInfo[order.id] = { count: 0, loading: false }
          }
        })
      )
      
      setOffersCountMap(offersCountInfo)

      // جلب حالات الطلبات من order_statuses collection
      const orderStatusInfo: Record<string, OrderStatusInfo> = {}
      await Promise.all(
        orders.map(async (order) => {
          orderStatusInfo[order.id] = { buyerStatus: null, sellerStatus: null, loading: true }
          try {
            // جلب حالة المشتري
            if (order.buyerId) {
              const buyerStatusDoc = await getDoc(doc(db, 'order_statuses', `${order.id}_${order.buyerId}`))
              if (buyerStatusDoc.exists()) {
                orderStatusInfo[order.id].buyerStatus = buyerStatusDoc.data().status || null
              }
            }

            // جلب حالة التاجر - نحتاج أولاً إلى العثور على العرض المقبول
            try {
              const offersQuery = query(
                collection(db, 'offers'),
                where('requestId', '==', order.id),
                where('status', '==', 'accepted')
              )
              const offersSnapshot = await getDocs(offersQuery)
              if (!offersSnapshot.empty) {
                const acceptedOffer = offersSnapshot.docs[0].data()
                const sellerId = acceptedOffer.sellerId
                if (sellerId) {
                  const sellerStatusDoc = await getDoc(doc(db, 'order_statuses', `${order.id}_${sellerId}`))
                  if (sellerStatusDoc.exists()) {
                    orderStatusInfo[order.id].sellerStatus = sellerStatusDoc.data().status || null
                  }
                }
              }
            } catch (error) {
              console.error(`خطأ في جلب حالة التاجر للطلب ${order.id}:`, error)
            }

            orderStatusInfo[order.id].loading = false
          } catch (error) {
            console.error(`خطأ في جلب حالات الطلب ${order.id}:`, error)
            orderStatusInfo[order.id].loading = false
          }
        })
      )
      
      setOrderStatusMap(orderStatusInfo)
    })
    return () => unsub()
  }, [])

  // فتح Dialog التفاصيل تلقائياً إذا كان هناك orderId في الـ URL
  useEffect(() => {
    const orderIdFromUrl = searchParams.get('orderId')
    const qFromUrl = (searchParams.get('q') || '').trim()
    if (qFromUrl && !searchQuery) {
      setSearchQuery(qFromUrl)
    }
    if (orderIdFromUrl && rows.length > 0) {
      // التحقق من وجود الطلب في القائمة
      const orderExists = rows.some(order => order.id === orderIdFromUrl)
      if (orderExists) {
        setOrderDetailsDialog({ open: true, orderId: orderIdFromUrl })
        // إزالة orderId من الـ URL بعد فتح Dialog
        searchParams.delete('orderId')
        setSearchParams(searchParams, { replace: true })
      } else if (!searchQuery) {
        setSearchQuery(orderIdFromUrl)
      }
    }
  }, [searchParams, rows, setSearchParams, searchQuery])

  // فلترة الطلبات
  const filteredRows = rows.filter(order => {
    // فلترة حسب الحالة
    if (statusFilter !== 'all' && order.status !== statusFilter) {
      return false
    }
    
    // فلترة حسب البحث
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase()
      const buyerInfo = buyerInfoMap[order.buyerId || '']
      const buyerName = buyerInfo?.name?.toLowerCase() || ''
      const buyerPhone = buyerInfo?.phone?.toLowerCase() || ''
      const productName = order.productName?.toLowerCase() || ''
      const orderId = order.id?.toLowerCase() || ''
      
      if (
        !orderId.includes(query) &&
        !buyerName.includes(query) &&
        !buyerPhone.includes(query) &&
        !productName.includes(query)
      ) {
        return false
      }
    }
    
    return true
  })

  const paginatedRows = filteredRows.slice((page - 1) * pageSize, page * pageSize)
  const totalPages = Math.ceil(filteredRows.length / pageSize)

  // تنسيق التاريخ والوقت
  const formatDateTime = (timestamp: any) => {
    if (!timestamp) return 'غير متوفر'
    
    try {
      let date: Date
      if (timestamp instanceof Timestamp) {
        date = timestamp.toDate()
      } else if (timestamp.toDate) {
        date = timestamp.toDate()
      } else if (timestamp instanceof Date) {
        date = timestamp
      } else {
        return 'غير متوفر'
      }
      
      const now = new Date()
      const diffMs = now.getTime() - date.getTime()
      const diffMins = Math.floor(diffMs / 60000)
      const diffHours = Math.floor(diffMs / 3600000)
      const diffDays = Math.floor(diffMs / 86400000)
      
      const timeStr = date.toLocaleTimeString('ar-EG', { hour: '2-digit', minute: '2-digit' })
      const dateStr = date.toLocaleDateString('ar-EG', { year: 'numeric', month: 'long', day: 'numeric' })
      
      if (diffMins < 1) return `الآن (${timeStr})`
      if (diffMins < 60) return `منذ ${diffMins} دقيقة (${timeStr})`
      if (diffHours < 24) return `منذ ${diffHours} ساعة (${timeStr})`
      if (diffDays < 7) return `منذ ${diffDays} يوم (${timeStr})`
      
      return `${dateStr} - ${timeStr}`
    } catch (error) {
      console.error('خطأ في تنسيق التاريخ:', error)
      return 'غير متوفر'
    }
  }

  // حذف طلب واحد
  const handleDeleteOrder = async (orderId: string) => {
    try {
      await deleteDoc(doc(db, 'requests', orderId))
      setSnackbar({ open: true, message: 'تم حذف الطلب بنجاح', severity: 'success' })
      setSelectedOrders(selectedOrders.filter(id => id !== orderId))
    } catch (error) {
      console.error('خطأ في حذف الطلب:', error)
      setSnackbar({ open: true, message: 'فشل حذف الطلب', severity: 'error' })
    }
  }

  // حذف طلبات متعددة
  const handleDeleteMultiple = async () => {
    try {
      await Promise.all(selectedOrders.map(orderId => deleteDoc(doc(db, 'requests', orderId))))
      setSnackbar({ open: true, message: `تم حذف ${selectedOrders.length} طلب بنجاح`, severity: 'success' })
      setSelectedOrders([])
      setDeleteDialog({ open: false })
    } catch (error) {
      console.error('خطأ في حذف الطلبات:', error)
      setSnackbar({ open: true, message: 'فشل حذف بعض الطلبات', severity: 'error' })
    }
  }

  // تحديد/إلغاء تحديد جميع الطلبات في الصفحة الحالية
  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedOrders([...new Set([...selectedOrders, ...paginatedRows.map(o => o.id)])])
    } else {
      const pageIds = new Set(paginatedRows.map(o => o.id))
      setSelectedOrders(selectedOrders.filter(id => !pageIds.has(id)))
    }
  }

  // التحقق من تحديد جميع الطلبات في الصفحة
  const allSelected = paginatedRows.length > 0 && paginatedRows.every(o => selectedOrders.includes(o.id))
  const someSelected = paginatedRows.some(o => selectedOrders.includes(o.id))

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
        <Typography variant="h5" fontWeight="bold">إدارة الطلبات</Typography>
        {selectedOrders.length > 0 && (
          <Button
            variant="contained"
            color="error"
            startIcon={<DeleteIcon />}
            onClick={() => setDeleteDialog({ open: true, multiple: true })}
          >
            حذف المحدد ({selectedOrders.length})
          </Button>
        )}
      </Stack>

      {/* شريط البحث والفلترة */}
      <Stack direction="row" spacing={2} sx={{ mb: 3 }} flexWrap="wrap" gap={2}>
        <TextField
          placeholder="بحث عن طلب..."
          value={searchQuery}
          onChange={(e) => {
            setSearchQuery(e.target.value)
            setPage(1)
          }}
          size="small"
          sx={{ minWidth: 250, flexGrow: 1 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            )
          }}
        />
        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel>حالة الطلب</InputLabel>
          <Select
            value={statusFilter}
            label="حالة الطلب"
            onChange={(e) => {
              setStatusFilter(e.target.value as OrderStatus)
              setPage(1)
            }}
          >
            <MenuItem value="all">جميع الحالات</MenuItem>
            <MenuItem value="pending">الطلب جديد</MenuItem>
            <MenuItem value="confirmed">تم الدفع</MenuItem>
            <MenuItem value="cancelled">تم حذف الطلب</MenuItem>
            <MenuItem value="completed">تم الاستلام</MenuItem>
            <MenuItem value="received">تم الاستلام</MenuItem>
            <MenuItem value="expired">منتهي الصلاحية</MenuItem>
          </Select>
        </FormControl>
        <Typography variant="body2" sx={{ alignSelf: 'center', color: 'text.secondary' }}>
          إجمالي الطلبات: {filteredRows.length}
        </Typography>
      </Stack>

      {/* جدول الطلبات */}
      <TableContainer component={Paper} sx={{ mb: 3 }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox" sx={{ width: 50 }}>
                <Checkbox
                  indeterminate={someSelected && !allSelected}
                  checked={allSelected}
                  onChange={(e) => handleSelectAll(e.target.checked)}
                />
              </TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>رقم الطلب</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>اسم المنتج</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>اسم المشتري</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>رقم الهاتف</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>اسم التاجر</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>هاتف التاجر</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>الحالة مشتري</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>الحالة تاجر</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>العروض المستلمة</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>تاريخ ووقت الطلب</TableCell>
              <TableCell sx={{ fontWeight: 'bold', width: 100 }}>الإجراءات</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedRows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={12} align="center" sx={{ py: 4 }}>
                  <Typography color="text.secondary">لا توجد طلبات</Typography>
                </TableCell>
              </TableRow>
            ) : (
              paginatedRows.map((order) => {
                const buyerInfo = buyerInfoMap[order.buyerId || '']
                const orderStatusInfo = orderStatusMap[order.id] || { buyerStatus: null, sellerStatus: null, loading: true }
                const offersInfo = offersCountMap[order.id] || { count: 0, loading: true }
                const sellerId = orderSellerMap[order.id]
                const sellerInfo = sellerId ? sellerInfoMap[sellerId] : undefined
                const isSelected = selectedOrders.includes(order.id)
                
                // الحصول على معلومات الحالة للمشتري
                const buyerStatus = orderStatusInfo.buyerStatus || order.status || 'pending'
                const buyerStatusInfo = statusMap[buyerStatus] || { label: buyerStatus || 'غير محدد', color: 'default' as const }
                
                // الحصول على معلومات الحالة للتاجر
                const sellerStatus = orderStatusInfo.sellerStatus || order.status || 'pending'
                const sellerStatusInfo = statusMap[sellerStatus] || { label: sellerStatus || 'غير محدد', color: 'default' as const }
                
                // رقم الطلب (المعرف الكامل للطلب)
                const orderNumber = order.id.toUpperCase()

                return (
                  <TableRow 
                    key={order.id} 
                    hover 
                    selected={isSelected}
                    onClick={() => setOrderDetailsDialog({ open: true, orderId: order.id })}
                    sx={{ cursor: 'pointer' }}
                  >
                    <TableCell padding="checkbox" onClick={(e) => e.stopPropagation()}>
                      <Checkbox
                        checked={isSelected}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedOrders([...selectedOrders, order.id])
                          } else {
                            setSelectedOrders(selectedOrders.filter(id => id !== order.id))
                          }
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight="medium" color="primary">
                        {orderNumber}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight="medium">
                        {order.productName || '—'}
                      </Typography>
                      {order.productCategory && (
                        <Typography variant="caption" color="text.secondary">
                          {order.productCategory}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {buyerInfo?.name || 'جاري التحميل...'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="primary">
                        {buyerInfo?.phone || 'جاري التحميل...'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {sellerInfo?.name || '—'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="primary">
                        {sellerInfo?.phone || '—'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {orderStatusInfo.loading ? (
                        <Typography variant="body2" color="text.secondary">
                          جاري التحميل...
                        </Typography>
                      ) : (
                        <Chip 
                          label={buyerStatusInfo.label} 
                          color={buyerStatusInfo.color}
                          size="small"
                        />
                      )}
                    </TableCell>
                    <TableCell>
                      {orderStatusInfo.loading ? (
                        <Typography variant="body2" color="text.secondary">
                          جاري التحميل...
                        </Typography>
                      ) : (
                        <Chip 
                          label={sellerStatusInfo.label} 
                          color={sellerStatusInfo.color}
                          size="small"
                        />
                      )}
                    </TableCell>
                    <TableCell>
                      {offersInfo.loading ? (
                        <Typography variant="body2" color="text.secondary">
                          جاري التحميل...
                        </Typography>
                      ) : (
                        <Button
                          size="small"
                          variant="text"
                          onClick={(e) => {
                            e.stopPropagation()
                            navigate(`/merchants-map?orderId=${encodeURIComponent(order.id)}`)
                          }}
                          sx={{ minWidth: 0, px: 1 }}
                        >
                          {offersInfo.count}
                        </Button>
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {formatDateTime(order.createdAt)}
                      </Typography>
                    </TableCell>
                    <TableCell onClick={(e) => e.stopPropagation()}>
                      <Stack direction="row" spacing={1}>
                        <Tooltip title="عرض التفاصيل">
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => setOrderDetailsDialog({ open: true, orderId: order.id })}
                          >
                            <VisibilityIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      <Tooltip title="حذف الطلب">
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => setDeleteDialog({ open: true, orderId: order.id })}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      </Stack>
                    </TableCell>
                  </TableRow>
                )
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Pagination */}
      {totalPages > 1 && (
        <Stack direction="row" justifyContent="center">
          <Pagination
            count={totalPages}
            page={page}
            onChange={(_, newPage) => setPage(newPage)}
            color="primary"
            size="large"
          />
        </Stack>
      )}

      {/* Dialog حذف طلب واحد */}
      <Dialog open={deleteDialog.open && !deleteDialog.multiple} onClose={() => setDeleteDialog({ open: false })}>
        <DialogTitle>تأكيد الحذف</DialogTitle>
        <DialogContent>
          <DialogContentText>
            هل أنت متأكد من حذف هذا الطلب؟ لا يمكن التراجع عن هذا الإجراء.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog({ open: false })}>إلغاء</Button>
          <Button 
            onClick={() => {
              if (deleteDialog.orderId) {
                handleDeleteOrder(deleteDialog.orderId)
                setDeleteDialog({ open: false })
              }
            }} 
            color="error" 
            variant="contained"
          >
            حذف
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog حذف طلبات متعددة */}
      <Dialog open={deleteDialog.open && deleteDialog.multiple === true} onClose={() => setDeleteDialog({ open: false })}>
        <DialogTitle>تأكيد الحذف</DialogTitle>
        <DialogContent>
          <DialogContentText>
            هل أنت متأكد من حذف {selectedOrders.length} طلب محدد؟ لا يمكن التراجع عن هذا الإجراء.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog({ open: false })}>إلغاء</Button>
          <Button 
            onClick={handleDeleteMultiple} 
            color="error" 
            variant="contained"
          >
            حذف ({selectedOrders.length})
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog تفاصيل الطلب */}
      <Dialog 
        open={orderDetailsDialog.open} 
        onClose={() => setOrderDetailsDialog({ open: false })}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Stack direction="row" spacing={2} alignItems="center">
            <DescriptionIcon color="primary" />
            <Typography variant="h6">تفاصيل الطلب</Typography>
          </Stack>
        </DialogTitle>
        <DialogContent>
          {orderDetailsDialog.orderId && (() => {
            const order = rows.find(o => o.id === orderDetailsDialog.orderId)
            if (!order) {
              return <Typography>الطلب غير موجود</Typography>
            }
            
            const buyerInfo = buyerInfoMap[order.buyerId || '']
            const orderStatusInfo = orderStatusMap[order.id] || { buyerStatus: null, sellerStatus: null, loading: true }
            const offersInfo = offersCountMap[order.id] || { count: 0, loading: true }
            const sellerId = orderSellerMap[order.id]
            const sellerInfo = sellerId ? sellerInfoMap[sellerId] : undefined
            
            const buyerStatus = orderStatusInfo.buyerStatus || order.status || 'pending'
            const buyerStatusInfo = statusMap[buyerStatus] || { label: buyerStatus || 'غير محدد', color: 'default' as const }
            const sellerStatus = orderStatusInfo.sellerStatus || order.status || 'pending'
            const sellerStatusInfo = statusMap[sellerStatus] || { label: sellerStatus || 'غير محدد', color: 'default' as const }
            
            return (
              <Stack spacing={3} sx={{ mt: 1 }}>
                {/* معلومات الطلب الأساسية */}
                <Box sx={{ 
                  p: 2, 
                  backgroundColor: 'grey.50', 
                  borderRadius: 2,
                  border: '1px solid',
                  borderColor: 'grey.200'
                }}>
                  <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                    معلومات الطلب
                  </Typography>
                  <Grid container spacing={2} sx={{ mt: 1 }}>
                    <Grid item xs={12} md={6}>
                      <Stack spacing={1}>
                        <Typography variant="caption" color="text.secondary">رقم الطلب:</Typography>
                        <Typography variant="body2" sx={{ fontFamily: 'monospace', wordBreak: 'break-all' }}>
                          {order.id}
                        </Typography>
                      </Stack>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Stack spacing={1}>
                        <Typography variant="caption" color="text.secondary">اسم المنتج:</Typography>
                        <Typography variant="body2" fontWeight="medium">
                          {order.productName || 'غير محدد'}
                        </Typography>
                      </Stack>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Stack spacing={1}>
                        <Typography variant="caption" color="text.secondary">الفئة:</Typography>
                        <Typography variant="body2">
                          {order.productCategory || 'غير محدد'}
                        </Typography>
                      </Stack>
                    </Grid>
                    {order.rangeKm && (
                      <Grid item xs={12} md={6}>
                        <Stack spacing={1}>
                          <Typography variant="caption" color="text.secondary">نطاق البحث:</Typography>
                          <Typography variant="body2">
                            {order.rangeKm} كم
                          </Typography>
                        </Stack>
                      </Grid>
                    )}
                    {order.location && (
                      <Grid item xs={12} md={6}>
                        <Stack spacing={1}>
                          <Typography variant="caption" color="text.secondary">الموقع:</Typography>
                          <Typography variant="body2">
                            {order.location.lat || order.location.latitude}, {order.location.lng || order.location.longitude}
                          </Typography>
                        </Stack>
                      </Grid>
                    )}
                    {order.additionalProductDetails && (
                      <Grid item xs={12}>
                        <Stack spacing={1}>
                          <Typography variant="caption" color="text.secondary">تفاصيل إضافية عن المنتج:</Typography>
                          <Box sx={{ 
                            p: 1.5, 
                            backgroundColor: 'white', 
                            borderRadius: 1,
                            border: '1px solid',
                            borderColor: 'grey.300'
                          }}>
                            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                              {order.additionalProductDetails}
                            </Typography>
                          </Box>
                        </Stack>
                      </Grid>
                    )}
                    {order.notes && (
                      <Grid item xs={12}>
                        <Stack spacing={1}>
                          <Typography variant="caption" color="text.secondary">ملاحظات:</Typography>
                          <Box sx={{ 
                            p: 1.5, 
                            backgroundColor: 'white', 
                            borderRadius: 1,
                            border: '1px solid',
                            borderColor: 'grey.300'
                          }}>
                            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                              {order.notes}
                            </Typography>
                          </Box>
                        </Stack>
                      </Grid>
                    )}
                  </Grid>
                </Box>

                {/* معلومات المشتري */}
                <Box sx={{ 
                  p: 2, 
                  backgroundColor: 'grey.50', 
                  borderRadius: 2,
                  border: '1px solid',
                  borderColor: 'grey.200'
                }}>
                  <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                    معلومات المشتري
                  </Typography>
                  <Grid container spacing={2} sx={{ mt: 1 }}>
                    <Grid item xs={12} md={6}>
                      <Stack spacing={1}>
                        <Typography variant="caption" color="text.secondary">الاسم:</Typography>
                        <Typography variant="body2">
                          {buyerInfo?.name || 'جاري التحميل...'}
                        </Typography>
                      </Stack>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Stack spacing={1}>
                        <Typography variant="caption" color="text.secondary">رقم الهاتف:</Typography>
                        <Typography variant="body2" color="primary">
                          {buyerInfo?.phone || 'جاري التحميل...'}
                        </Typography>
                      </Stack>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Stack spacing={1}>
                        <Typography variant="caption" color="text.secondary">حالة الطلب (المشتري):</Typography>
                        <Chip 
                          label={buyerStatusInfo.label} 
                          color={buyerStatusInfo.color}
                          size="small"
                        />
                      </Stack>
                    </Grid>
                  </Grid>
                </Box>

                {/* معلومات التاجر */}
                {sellerInfo && (
                  <Box sx={{ 
                    p: 2, 
                    backgroundColor: 'grey.50', 
                    borderRadius: 2,
                    border: '1px solid',
                    borderColor: 'grey.200'
                  }}>
                    <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                      معلومات التاجر
                    </Typography>
                    <Grid container spacing={2} sx={{ mt: 1 }}>
                      <Grid item xs={12} md={6}>
                        <Stack spacing={1}>
                          <Typography variant="caption" color="text.secondary">الاسم:</Typography>
                          <Typography variant="body2">
                            {sellerInfo.name || 'غير محدد'}
                          </Typography>
                        </Stack>
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <Stack spacing={1}>
                          <Typography variant="caption" color="text.secondary">رقم الهاتف:</Typography>
                          <Typography variant="body2" color="primary">
                            {sellerInfo.phone || 'غير متوفر'}
                          </Typography>
                        </Stack>
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <Stack spacing={1}>
                          <Typography variant="caption" color="text.secondary">حالة الطلب (التاجر):</Typography>
                          <Chip 
                            label={sellerStatusInfo.label} 
                            color={sellerStatusInfo.color}
                            size="small"
                          />
                        </Stack>
                      </Grid>
                    </Grid>
                  </Box>
                )}

                {/* العروض المستلمة */}
                <Box sx={{ 
                  p: 2, 
                  backgroundColor: 'grey.50', 
                  borderRadius: 2,
                  border: '1px solid',
                  borderColor: 'grey.200'
                }}>
                  <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                    العروض
                  </Typography>
                  <Grid container spacing={2} sx={{ mt: 1 }}>
                    <Grid item xs={12} md={6}>
                      <Stack spacing={1}>
                        <Typography variant="caption" color="text.secondary">العروض المستلمة:</Typography>
                        {offersInfo.loading ? (
                          <Typography variant="body2" color="text.secondary">جاري التحميل...</Typography>
                        ) : (
                          <Stack direction="row" spacing={1} alignItems="center">
                            <Typography variant="body2" fontWeight="medium" color="primary">
                              {offersInfo.count}
                            </Typography>
                            <Button
                              size="small"
                              variant="outlined"
                              onClick={() => navigate(`/merchants-map?orderId=${encodeURIComponent(order.id)}`)}
                            >
                              فتح على خريطة التجار
                            </Button>
                          </Stack>
                        )}
                      </Stack>
                    </Grid>
                  </Grid>
                </Box>

                {/* التواريخ */}
                <Box sx={{ 
                  p: 2, 
                  backgroundColor: 'grey.50', 
                  borderRadius: 2,
                  border: '1px solid',
                  borderColor: 'grey.200'
                }}>
                  <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                    التواريخ
                  </Typography>
                  <Grid container spacing={2} sx={{ mt: 1 }}>
                    <Grid item xs={12} md={6}>
                      <Stack spacing={1}>
                        <Typography variant="caption" color="text.secondary">تاريخ الإنشاء:</Typography>
                        <Typography variant="body2">
                          {formatDateTime(order.createdAt)}
                        </Typography>
                      </Stack>
                    </Grid>
                    {order.updatedAt && (
                      <Grid item xs={12} md={6}>
                        <Stack spacing={1}>
                          <Typography variant="caption" color="text.secondary">آخر تحديث:</Typography>
                          <Typography variant="body2">
                            {formatDateTime(order.updatedAt)}
                          </Typography>
                        </Stack>
                      </Grid>
                    )}
                    {order.expiresAt && (
                      <Grid item xs={12} md={6}>
                        <Stack spacing={1}>
                          <Typography variant="caption" color="text.secondary">تاريخ الانتهاء:</Typography>
                          <Typography variant="body2">
                            {formatDateTime(order.expiresAt)}
                          </Typography>
                        </Stack>
                      </Grid>
                    )}
                  </Grid>
                </Box>
              </Stack>
            )
          })()}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOrderDetailsDialog({ open: false })}>إغلاق</Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar للإشعارات */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  )
}
